import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useApp } from "@/contexts/AppContext";

interface Notification {
  id: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  link?: string;
}

/** Uma conversa com mensagens ainda nao lidas (some do sininho quando a conversa e aberta) */
interface MsgItem {
  conversationId: string;
  name: string;
  listingTitle: string;
  count: number;
  lastAt: string;
  preview: string;
}

type Item =
  | { type: "notif"; at: number; n: Notification }
  | { type: "msg"; at: number; m: MsgItem };

// "visto" das mensagens fica no aparelho: depois de abrir o sininho elas ficam cinza
const seenKey = (uid: string) => `agro:bell-msgs-seen:${uid}`;
function readSeen(uid: string) {
  try { return Number(localStorage.getItem(seenKey(uid))) || 0; } catch { return 0; }
}
function writeSeen(uid: string, t: number) {
  try { localStorage.setItem(seenKey(uid), String(t)); } catch { /* sem armazenamento */ }
}

const fmt = (s: string) =>
  new Date(s).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function NotificationBell() {
  const { user } = useApp();
  const uid = user?.id;
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [msgs, setMsgs] = useState<MsgItem[]>([]);
  const [seenAt, setSeenAt] = useState(0);          // ultimo momento em que o sininho foi aberto
  const [shownSeenAt, setShownSeenAt] = useState(0); // valor anterior, usado enquanto esta aberto
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    if (!uid) return;
    const { data, error } = await supabase
      .from("notifications")
      .select("id, title, message, read, created_at, link")
      .eq("user_id", uid)
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) console.error("sininho:", error.message);
    if (data) setNotifications(data as Notification[]);
  }, [uid]);

  const fetchMessages = useCallback(async () => {
    if (!uid) return;
    const { data: convs, error } = await supabase
      .from("conversations")
      .select(`id, buyer_id, seller_id,
        listing:listings(title),
        buyer:profiles!conversations_buyer_id_fkey(name),
        seller:profiles!conversations_seller_id_fkey(name)`)
      .or(`buyer_id.eq.${uid},seller_id.eq.${uid}`);
    if (error) console.error("sininho (conversas):", error.message);
    if (!convs?.length) { setMsgs([]); return; }

    const { data: unread } = await supabase
      .from("messages")
      .select("conversation_id, content, created_at")
      .in("conversation_id", convs.map((c: any) => c.id))
      .neq("sender_id", uid)
      .or("read.eq.false,read.is.null")
      .order("created_at", { ascending: false })
      .limit(300);

    const byConv = new Map<string, MsgItem>();
    for (const m of (unread ?? []) as any[]) {
      const prev = byConv.get(m.conversation_id);
      if (prev) { prev.count++; continue; }
      const c: any = convs.find((x: any) => x.id === m.conversation_id);
      if (!c) continue;
      const other = c.buyer_id === uid ? c.seller : c.buyer;
      byConv.set(m.conversation_id, {
        conversationId: c.id,
        name: (Array.isArray(other) ? other[0]?.name : other?.name) || "Usuário",
        listingTitle: (Array.isArray(c.listing) ? c.listing[0]?.title : c.listing?.title) || "",
        count: 1,
        lastAt: m.created_at,
        preview: String(m.content || ""),
      });
    }
    setMsgs([...byConv.values()]);
  }, [uid]);

  useEffect(() => {
    if (!uid) { setNotifications([]); setMsgs([]); return; }
    setSeenAt(readSeen(uid));
    fetchNotifications();
    fetchMessages();

    let timer: ReturnType<typeof setTimeout> | undefined;
    const refreshMsgs = () => { clearTimeout(timer); timer = setTimeout(fetchMessages, 400); };

    const channel = supabase.channel(`notif-bell-${uid}-${Math.random().toString(36).slice(2)}`);
    channel
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "notifications",
        filter: `user_id=eq.${uid}`,
      }, (payload) => {
        setNotifications((prev) => {
          const n = payload.new as Notification;
          if (prev.some((x) => x.id === n.id)) return prev;
          return [n, ...prev];
        });
      })
      // mensagem nova ou marcada como lida (so chegam as conversas da propria pessoa)
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, refreshMsgs)
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === "visible") { fetchNotifications(); fetchMessages(); }
    };
    window.addEventListener("agro:messages-read", refreshMsgs);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
      window.removeEventListener("agro:messages-read", refreshMsgs);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [uid, fetchNotifications, fetchMessages]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function toggle() {
    if (!open && uid) {
      // ao abrir: o que ja estava ali passa a contar como visto (fica cinza na proxima vez)
      setShownSeenAt(seenAt);
      const now = Date.now();
      setSeenAt(now);
      writeSeen(uid, now);
      fetchNotifications();
      fetchMessages();
    }
    setOpen((o) => !o);
  }

  async function markAllRead() {
    if (!uid) return;
    await supabase.from("notifications").update({ read: true }).eq("user_id", uid).eq("read", false);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  async function markRead(id: string) {
    await supabase.from("notifications").update({ read: true }).eq("id", id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }

  if (!user) return null;

  const unreadNotifs = notifications.filter((n) => !n.read).length;
  const newMsgs = msgs.filter((m) => Date.parse(m.lastAt) > seenAt).length;
  const badge = unreadNotifs + newMsgs;

  const items: Item[] = [
    ...msgs.map((m) => ({ type: "msg" as const, at: Date.parse(m.lastAt), m })),
    ...notifications.map((n) => ({ type: "notif" as const, at: Date.parse(n.created_at), n })),
  ].sort((a, b) => b.at - a.at);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Notificações"
      >
        <Bell className="h-5 w-5 text-white" />
        {badge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-destructive text-white text-xs font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-semibold text-sm text-foreground">Notificações</span>
            {unreadNotifs > 0 && (
              <button onClick={markAllRead} className="text-xs text-primary hover:underline">
                Marcar todas como lidas
              </button>
            )}
          </div>
          <div className="max-h-[70vh] sm:max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma notificação</div>
            ) : (
              items.map((it) => {
                if (it.type === "msg") {
                  const m = it.m;
                  const seen = it.at <= shownSeenAt; // ja visto no sininho, mas a conversa ainda nao foi aberta
                  return (
                    <div
                      key={`m-${m.conversationId}`}
                      onClick={() => { setOpen(false); navigate(`/mensagens?c=${m.conversationId}`); }}
                      className={`px-4 py-3 border-b border-border cursor-pointer hover:bg-secondary transition-colors ${seen ? "" : "bg-primary/5"}`}
                    >
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${seen ? "bg-muted text-muted-foreground" : "bg-primary/15 text-primary"}`}>
                          <MessageCircle className="h-3.5 w-3.5" />
                        </span>
                        <div className={`min-w-0 flex-1 ${seen ? "opacity-60" : ""}`}>
                          <p className="text-sm font-medium text-foreground">
                            {m.count > 1 ? `${m.count} mensagens de ${m.name}` : `Mensagem de ${m.name}`}
                          </p>
                          <p className="text-xs text-foreground/70 mt-0.5 leading-relaxed line-clamp-2">{m.preview}</p>
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {fmt(m.lastAt)}{m.listingTitle ? ` · ${m.listingTitle}` : ""}
                          </p>
                        </div>
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${seen ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground"}`}>
                          não lida
                        </span>
                      </div>
                    </div>
                  );
                }
                const n = it.n;
                return (
                  <div
                    key={n.id}
                    onClick={async () => { await markRead(n.id); setOpen(false); navigate(n.link || "/dashboard"); }}
                    className={`px-4 py-3 border-b border-border cursor-pointer hover:bg-secondary transition-colors ${!n.read ? "bg-primary/5" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />}
                      <div className={!n.read ? "" : "ml-4 opacity-60"}>
                        <p className="text-sm font-medium text-foreground">{n.title}</p>
                        <p className="text-xs text-foreground/70 mt-0.5 leading-relaxed">{n.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">{fmt(n.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
